import { forwardRef, useState, useRef, useEffect, useLayoutEffect, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";
import cn from "mcn";
import { IoSearch } from "react-icons/io5";
import { FaXmark } from "react-icons/fa6";
import { CaretDownIcon } from "../icons/CaretDownIcon";
import type { TOption } from "../components/Autocomplete/Autocomplete";
import ClickAwayListener from "../components/ClickAwayListener";
import { Tooltip, TooltipTrigger } from "@artis/ui";

// Module-level variable to persist isOpen state across remounts
let persistedIsOpen = false;

export type AutocompleteOption = {
  value: string;
  label: string;
  groupValue: string;
  disabled?: boolean;
  /** Explains why a disabled option cannot be picked; shown on hover anywhere in the row. */
  disabledTooltip?: string;
  readonlySelected?: boolean;
  shortName?: string;
  longName?: string | null;
};

export type AutocompleteProps = {
  options: AutocompleteOption[];
  value?: string;
  selectedValues?: string[];
  onChange?: (value: string) => void;
  onSelectAll?: (groupValue: string, allSelected: boolean) => void;
  error?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoComplete?: boolean;
  keepOpenOnSelect?: boolean;
  maxHeight?: number;
  highlightSearch?: boolean;
  /**
   * Render the options panel in a portal on document.body with fixed positioning.
   * Use inside modals / overflow containers so the list is not clipped.
   */
  portal?: boolean;
  /**
   * Show selected values as removable chip tags inside the input area.
   * Clicking the × on a chip calls onChange with that value to deselect it.
   * Requires selectedValues to be provided.
   */
  showSelectedTags?: boolean;
  /** Title-case muted group labels instead of uppercase sticky section bars. */
  subtleGroupHeaders?: boolean;
  /** Show an X to clear the selected value. Calls onChange with an empty string. */
  clearable?: boolean;
  /**
   * Curve Management Commodity combobox only. Keeps parent out of menu rows,
   * paints Commodity (Parent) in the input, and uses pointer-accurate highlight
   * so other AutocompleteComponent call sites stay unchanged.
   */
  commodityMenu?: boolean;
};

export const AutocompleteComponent = forwardRef<HTMLDivElement, AutocompleteProps>(function AutocompleteComponent(
  {
    options,
    value,
    selectedValues,
    onChange,
    onSelectAll,
    error,
    fullWidth,
    placeholder,
    disabled,
    className,
    autoComplete = true,
    keepOpenOnSelect = false,
    maxHeight = 400,
    highlightSearch = false,
    portal = false,
    showSelectedTags = false,
    subtleGroupHeaders = false,
    clearable = false,
    commodityMenu = false,
  },
  _ref,
) {
  // Use module-level variable to persist isOpen state across remounts (only when keepOpenOnSelect is true)
  const [isOpen, setIsOpenState] = useState(() => (keepOpenOnSelect ? persistedIsOpen : false));

  const setIsOpen = useCallback(
    (value: boolean) => {
      if (keepOpenOnSelect) {
        persistedIsOpen = value;
      }
      setIsOpenState(value);
    },
    [keepOpenOnSelect],
  );

  useEffect(() => {
    // Restore isOpen state from module variable if component remounts and keepOpenOnSelect is enabled
    if (keepOpenOnSelect && persistedIsOpen) {
      setIsOpenState(true);
    }
  }, [keepOpenOnSelect]);
  const [searchValue, setSearchValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState(maxHeight);
  const [portalListStyle, setPortalListStyle] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const safeZone = 27; // keep dropdown above the footer by at least 27px
  const shouldScrollRef = useRef(false);

  const selectedOption = value ? options.find((opt) => opt.value === value) : undefined;
  const effectiveSelectedValues = selectedValues ?? (value ? [value] : undefined);

  const filteredOptions = filterOptions(options, {
    inputValue: searchValue,
    getOptionLabel: (option) => (option as AutocompleteOption).label,
  });

  const groupedOptions = filteredOptions.reduce(
    (acc, option) => {
      const group = option.groupValue || "Other";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(option);
      return acc;
    },
    {} as Record<string, AutocompleteOption[]>,
  );

  const flatOptions = Object.entries(groupedOptions).flatMap(([_group, opts]) => opts);

  // Stable boolean dep: re-run only when the portal'd list mounts/unmounts, not on every reposition.
  const isPortalListMounted = portalListStyle !== null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: see isPortalListMounted above
  useEffect(() => {
    if (isOpen && inputRef.current && listRef.current) {
      const scrollPosition = window.scrollY || window.pageYOffset;
      inputRef.current.focus({ preventScroll: true });

      const listContainer = listRef.current;
      let isInitialOpen = true;
      const initialScrollTop = listContainer.scrollTop;

      const preventScroll = (e: Event) => {
        if (isInitialOpen) {
          e.preventDefault();
          e.stopPropagation();
          listContainer.scrollTop = initialScrollTop;
        }
      };

      listContainer.addEventListener("scroll", preventScroll, { passive: false, capture: true });

      const timeoutId = setTimeout(() => {
        isInitialOpen = false;
        listContainer.removeEventListener("scroll", preventScroll, true);
      }, 100);

      requestAnimationFrame(() => {
        if (window.scrollY !== scrollPosition || window.pageYOffset !== scrollPosition) {
          window.scrollTo({ top: scrollPosition, behavior: "instant" });
        }
      });

      return () => {
        clearTimeout(timeoutId);
        listContainer.removeEventListener("scroll", preventScroll, true);
      };
    }
  }, [isOpen, portal, isPortalListMounted]);

  useEffect(() => {
    if (!isOpen) setHighlightedIndex(-1);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setSearchValue("");
    if (!keepOpenOnSelect) {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setIsOpen(true);
        if (e.key === "ArrowDown") setHighlightedIndex(0);
        if (e.key === "ArrowUp") setHighlightedIndex(Math.max(0, flatOptions.length - 1));
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (flatOptions.length === 0) return -1;
          if (prev < 0) return 0;
          return Math.min(prev + 1, flatOptions.length - 1);
        });
        shouldScrollRef.current = true;
        break;
      case "ArrowUp":
        e.preventDefault();
        shouldScrollRef.current = true;
        setHighlightedIndex((prev) => {
          if (flatOptions.length === 0) return -1;
          if (prev < 0) return flatOptions.length - 1;
          return Math.max(prev - 1, 0);
        });
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && flatOptions[highlightedIndex]) {
          handleSelect(flatOptions[highlightedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchValue("");
        break;
    }
  };

  useEffect(() => {
    if (isOpen && listRef.current && flatOptions.length > 0 && shouldScrollRef.current) {
      const targetOption = flatOptions[highlightedIndex];
      if (targetOption) {
        const optionButton = listRef.current.querySelector(
          `button[data-option-value="${targetOption.value}"]`,
        ) as HTMLElement | null;
        if (commodityMenu) {
          optionButton?.scrollIntoView({ block: "nearest" });
        } else if (optionButton && listRef.current) {
          const container = listRef.current;
          const buttonRect = optionButton.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          if (buttonRect.top < containerRect.top) {
            container.scrollTop -= containerRect.top - buttonRect.top + 8;
          } else if (buttonRect.bottom > containerRect.bottom) {
            container.scrollTop += buttonRect.bottom - containerRect.bottom + 8;
          }
        }
      }
      shouldScrollRef.current = false;
    }
  }, [highlightedIndex, isOpen, flatOptions, commodityMenu]);

  useEffect(() => {
    if (!isOpen || portal) return;
    const updateMaxHeight = () => {
      if (!listRef.current) return;
      const rect = listRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - safeZone; // reserve safe zone for footer
      // Clamp to the caller-provided maxHeight (default 400) and keep a sensible minimum.
      const clamped = Math.max(120, Math.min(maxHeight, available - 16)); // subtract padding
      setDynamicMaxHeight(clamped);
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [isOpen, maxHeight, portal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure anchor when options/search change layout
  useLayoutEffect(() => {
    if (!isOpen || !portal) {
      setPortalListStyle(null);
      return;
    }
    const update = () => {
      const anchor = dropdownRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - safeZone;
      const spaceAbove = rect.top - safeZone;
      const maxWant = Math.min(maxHeight, 400);
      const flipUp = spaceBelow < Math.min(maxWant, 200) && spaceAbove > spaceBelow;
      const maxH = flipUp
        ? Math.max(120, Math.min(maxWant, spaceAbove - 16))
        : Math.max(120, Math.min(maxWant, spaceBelow - 16));
      const top = flipUp ? Math.max(4, rect.top - maxH - 4) : rect.bottom + 4;
      const left = Math.min(rect.left, Math.max(0, window.innerWidth - rect.width - 8));
      setPortalListStyle({ top, left, width: rect.width, maxHeight: maxH });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      setPortalListStyle(null);
    };
  }, [isOpen, portal, maxHeight, flatOptions.length, searchValue, selectedValues?.length]);

  function renderAutocompleteOptions(): ReactNode {
    return (
      <>
        {flatOptions.length === 0 ? (
          <div
            className="px-3 py-2 text-[var(--color-foreground-muted)]"
            style={{ fontSize: "var(--font-size-base, 12px)" }}
          >
            No options
          </div>
        ) : (
          Object.entries(groupedOptions).map(([groupValue, groupOptions]) => {
            const isGroupDisabled = groupOptions.every((opt) => opt.disabled);
            const enabledOptions = groupOptions.filter((opt) => !opt.disabled);
            const allSelected =
              enabledOptions.length > 0 && enabledOptions.every((opt) => selectedValues?.includes(opt.value));
            const showHeader = groupValue !== "Other" && groupValue !== "";
            return (
              <div key={groupValue}>
                {showHeader && (
                  <div
                    className={
                      subtleGroupHeaders
                        ? "flex items-center px-2 py-1 sticky top-0 z-10 bg-[var(--color-card)] text-[12px] leading-[18px] font-medium text-[var(--color-foreground-muted,#4D4D4D)]"
                        : "flex items-center justify-between px-3 sticky top-0 z-10 bg-card-muted uppercase h-[33px] min-h-[33px] text-[length:var(--font-size-base,12px)]"
                    }
                  >
                    <span
                      className={
                        subtleGroupHeaders
                          ? undefined
                          : "text-[var(--foreground-default,var(--color-foreground))] font-medium"
                      }
                    >
                      {groupValue}
                    </span>
                    {onSelectAll && !subtleGroupHeaders && (
                      <button
                        type="button"
                        disabled={isGroupDisabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isGroupDisabled) {
                            onSelectAll(groupValue, allSelected);
                          }
                        }}
                        className="bg-transparent text-[var(--color-neutral)] underline border border-transparent hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)] focus:bg-[var(--neutral-tonal)] focus:text-[var(--neutral-on-tonal)] focus:border-[var(--neutral-default)] active:bg-transparent active:text-[var(--neutral-click)] disabled:bg-[var(--neutral-disabled)] disabled:text-[var(--neutral-on-disabled)] px-[15px] h-[27px] text-base rounded-lg cursor-pointer focus:outline-none transition-colors duration-150 flex items-center justify-center"
                        style={{ fontWeight: "var(--font-weights-medium, 500)" }}
                      >
                        {allSelected ? "Remove All" : "Select All"}
                      </button>
                    )}
                  </div>
                )}
                {groupOptions.map((option) => {
                  const index = flatOptions.findIndex((opt) => opt.value === option.value);
                  const isSelected = effectiveSelectedValues?.includes(option.value) ?? false;
                  const showCheckmark = isSelected || option.readonlySelected;
                  const checkmarkColor =
                    option.readonlySelected && !isSelected
                      ? "var(--color-primary-on-disabled)"
                      : "var(--color-primary)";
                  const button = (
                    <button
                      type="button"
                      data-option-value={option.value}
                      aria-disabled={option.disabled ? true : undefined}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (!option.disabled) {
                          handleSelect(option.value);
                        }
                      }}
                      onMouseEnter={() => {
                        if (!commodityMenu) setHighlightedIndex(index);
                      }}
                      onMouseMove={() => {
                        if (commodityMenu && !option.disabled && index !== highlightedIndex) {
                          setHighlightedIndex(index);
                        }
                      }}
                      className={cn(
                        "w-full text-left pl-5 pr-3 transition-colors break-words relative flex items-center flex-wrap",
                        option.disabled
                          ? "cursor-not-allowed text-[var(--color-neutral-on-disabled)]"
                          : cn(
                              "text-[var(--foreground-default,var(--color-foreground))]",
                              !commodityMenu && "hover:bg-[var(--neutral-tonal)]",
                            ),
                        !option.disabled && index === highlightedIndex && "bg-[var(--neutral-tonal)]",
                        showCheckmark && "pr-8",
                      )}
                      style={{
                        fontSize: "var(--font-size-base, 12px)",
                        minHeight: "27px",
                        paddingTop: "2px",
                        paddingBottom: "2px",
                      }}
                    >
                      {(() => {
                        const hasExplicitNames = option.shortName !== undefined || option.longName !== undefined;
                        const shortName = option.shortName || option.label.replace(/:\s*[^:]+$/, "");
                        const longName = option.longName !== undefined ? option.longName : null;

                        if (hasExplicitNames) {
                          if (longName && longName !== shortName && !commodityMenu) {
                            return (
                              <span className="inline-block">
                                <span
                                  className={
                                    option.disabled
                                      ? "text-[var(--color-neutral-on-disabled)]"
                                      : "text-[var(--foreground-default,var(--color-foreground))]"
                                  }
                                >
                                  {highlightLabelParts(shortName, searchValue, highlightSearch)}
                                </span>
                                <span
                                  className={
                                    option.disabled
                                      ? "text-[var(--color-neutral-on-disabled)]"
                                      : "text-[var(--color-foreground-muted)]"
                                  }
                                  style={{ marginLeft: "4px" }}
                                >
                                  {"("}
                                  {highlightLabelParts(longName, searchValue, highlightSearch)}
                                  {")"}
                                </span>
                              </span>
                            );
                          }

                          return (
                            <span
                              className={
                                option.disabled
                                  ? "text-[var(--color-neutral-on-disabled)]"
                                  : "text-[var(--foreground-default,var(--color-foreground))]"
                              }
                            >
                              {highlightLabelParts(shortName, searchValue, highlightSearch)}
                            </span>
                          );
                        }

                        const label = option.label;
                        const match = label.match(/^(.+?)\s*\((.+?)\)$/);
                        if (match) {
                          const [, name, username] = match;
                          return (
                            <span className="inline-block">
                              <span
                                className={
                                  option.disabled
                                    ? "text-[var(--color-neutral-on-disabled)]"
                                    : "text-[var(--foreground-default,var(--color-foreground))]"
                                }
                              >
                                {name}
                              </span>
                              <span
                                className={
                                  option.disabled
                                    ? "text-[var(--color-neutral-on-disabled)]"
                                    : "text-[var(--color-foreground-muted)]"
                                }
                                style={{ marginLeft: "4px" }}
                              >
                                {"("}
                                {username}
                                {")"}
                              </span>
                            </span>
                          );
                        }
                        return (
                          <span
                            className={
                              option.disabled
                                ? "text-[var(--color-neutral-on-disabled)]"
                                : "text-[var(--foreground-default,var(--color-foreground))]"
                            }
                          >
                            {highlightLabelParts(label, searchValue, highlightSearch)}
                          </span>
                        );
                      })()}
                      {showCheckmark && (
                        <span
                          className="absolute right-2 top-1/2 -translate-y-1/2"
                          style={{
                            color: checkmarkColor,
                            fontSize: "12px",
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </button>
                  );
                  if (option.disabled && option.disabledTooltip) {
                    return (
                      <TooltipTrigger key={option.value} placement="top" origin="center" block>
                        {button}
                        <Tooltip>{option.disabledTooltip}</Tooltip>
                      </TooltipTrigger>
                    );
                  }
                  return (
                    <span key={option.value} className="block">
                      {button}
                    </span>
                  );
                })}
              </div>
            );
          })
        )}
      </>
    );
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (isOpen) {
          setIsOpen(false);
          setSearchValue("");
        }
      }}
    >
      <div ref={dropdownRef} className={cn("relative", fullWidth && "w-full", className)}>
        <div
          className={cn(
            "w-full rounded-lg border px-2 transition-colors flex gap-1",
            showSelectedTags ? "py-1 min-h-[32px]" : "py-[4.5px] min-h-[27px]",
            "items-center",
            "bg-[var(--color-surface-1)]",
            "text-[var(--foreground-default,var(--color-foreground))]",
            "border-[var(--outline-default)]",
            "hover:bg-[var(--color-surface-2)] hover:border-[var(--color-outline-variant)]",
            "disabled:cursor-not-allowed disabled:bg-[var(--colour-surface-disabled,#F0F0F0)] disabled:border-[var(--colour-surface-disabled,#F0F0F0)] disabled:text-[var(--colour-text-on-disabled,#A3A3A3)]",
            error && "border-[var(--color-danger)]",
            disabled && "pointer-events-none",
          )}
          style={{ fontSize: "var(--font-size-base, 12px)" }}
          onClick={() => !disabled && !isOpen && setIsOpen(true)}
        >
          {autoComplete && (
            <IoSearch className="text-[var(--color-foreground-muted)] shrink-0" style={{ fontSize: "1rem" }} />
          )}

          <div
            className={cn(
              "flex flex-wrap gap-1 items-center flex-1 min-w-0",
              showSelectedTags && selectedValues && selectedValues.length > 0
                ? "max-h-[98px] overflow-y-auto"
                : showSelectedTags
                  ? "h-[25px]"
                  : undefined,
            )}
          >
            {showSelectedTags &&
              selectedValues &&
              selectedValues.map((val) => {
                const opt = options.find((o) => o.value === val);
                if (!opt) return null;
                const displayLabel = opt.shortName || opt.label;
                return (
                  <div
                    key={val}
                    className="flex items-center overflow-hidden rounded-full border border-outline h-[18px] px-[4px] max-w-full shrink-0"
                  >
                    <span className="text-[10.5px] font-medium mr-1 whitespace-nowrap overflow-ellipsis overflow-hidden flex items-center text-[var(--neutral-default)]">
                      <span className="px-[1px]" style={{ fontWeight: "var(--font-weights-medium, 500)" }}>
                        {displayLabel}
                      </span>
                    </span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange?.(val);
                        }}
                        className="cursor-pointer p-0.5 rounded-[4px] border border-transparent hover:bg-[var(--neutral-tonal)] hover:text-[var(--neutral-on-tonal)] transition-colors"
                      >
                        <FaXmark className="text-[var(--neutral-default)] w-[8px] h-[8px]" />
                      </button>
                    </div>
                  </div>
                );
              })}

            <div className="relative flex-1 min-w-0">
              {(() => {
                const overlayWhenClosed = commodityMenu && !showSelectedTags && !!selectedOption && !searchValue;
                const overlayWhenOpen = !showSelectedTags && isOpen && !searchValue && !!selectedOption;
                const overlayActive = overlayWhenClosed || overlayWhenOpen;
                const shortName = selectedOption
                  ? selectedOption.shortName || selectedOption.label.replace(/:\s*[^:]+$/, "")
                  : "";
                const longName = selectedOption?.longName !== undefined ? selectedOption.longName : null;

                return (
                  <>
                    <input
                      ref={inputRef}
                      type="text"
                      disabled={disabled}
                      value={(() => {
                        if (overlayActive) return " ";
                        if (isOpen) return searchValue;
                        if (showSelectedTags) return searchValue;
                        if (!selectedOption) return "";
                        return longName && longName !== shortName ? `${shortName} (${longName})` : shortName;
                      })()}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        setHighlightedIndex(-1);
                      }}
                      onKeyDown={handleKeyDown}
                      onClick={() => !disabled && !isOpen && setIsOpen(true)}
                      placeholder={showSelectedTags && selectedValues && selectedValues.length > 0 ? "" : placeholder}
                      className={cn(
                        "w-full bg-transparent border-none truncate",
                        showSelectedTags ? "text-sm" : "text-[length:var(--font-size-base,12px)]",
                        "text-[var(--foreground-default,var(--color-foreground))]",
                        "placeholder:text-[var(--color-foreground-muted)]",
                        "focus:outline-none focus:ring-0",
                        overlayActive && "text-transparent",
                      )}
                      style={{
                        minWidth: 0,
                        ...(!showSelectedTags ? { fontSize: "var(--font-size-base, 12px)" } : {}),
                        ...(overlayActive ? { caretColor: "var(--color-foreground)" } : {}),
                      }}
                    />
                    {overlayActive && (
                      <div
                        className="absolute inset-0 flex items-center pointer-events-none truncate px-0"
                        style={{ fontSize: "var(--font-size-base, 12px)" }}
                      >
                        {longName && longName !== shortName ? (
                          <>
                            <span className="text-foreground truncate">{shortName}</span>
                            <span className="text-foreground-muted ml-1 truncate">({longName})</span>
                          </>
                        ) : (
                          <span className="text-foreground truncate">{shortName}</span>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {clearable && selectedOption && !disabled && (
            <button
              type="button"
              aria-label="Clear selection"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.("");
                setSearchValue("");
              }}
              className="flex items-center justify-center cursor-pointer shrink-0 p-0.5 rounded-[4px] border border-transparent hover:bg-[var(--neutral-tonal)]"
            >
              <FaXmark className="text-[var(--color-foreground-muted)] w-[10px] h-[10px]" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) {
                setIsOpen(!isOpen);
              }
            }}
            className="flex items-center justify-center cursor-pointer transition-transform shrink-0"
            style={{
              color: disabled ? "var(--color-neutral-on-disabled)" : "var(--color-neutral)",
              transform: isOpen ? "scale(0.7) rotate(180deg)" : "scale(0.7)",
            }}
          >
            <CaretDownIcon />
          </button>
        </div>

        {isOpen &&
          !disabled &&
          (portal && portalListStyle ? (
            createPortal(
              <div
                ref={listRef}
                data-click-away-inside
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }}
                onMouseLeave={() => {
                  if (commodityMenu) setHighlightedIndex(-1);
                }}
                className={cn(
                  "rounded-lg border overflow-y-auto overflow-x-hidden shadow-lg scrollbar-thin",
                  "bg-[var(--color-card)] border-[var(--outline-default)]",
                )}
                style={{
                  position: "fixed",
                  top: portalListStyle.top,
                  left: portalListStyle.left,
                  width: portalListStyle.width,
                  maxHeight: portalListStyle.maxHeight,
                  zIndex: 99999,
                }}
              >
                {renderAutocompleteOptions()}
              </div>,
              document.body,
            )
          ) : !portal ? (
            <div
              ref={listRef}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
              }}
              onMouseLeave={() => {
                if (commodityMenu) setHighlightedIndex(-1);
              }}
              className={cn(
                "absolute z-50 w-full mt-[2px] rounded-lg border overflow-y-auto overflow-x-hidden shadow-lg scrollbar-thin",
                "bg-[var(--color-card)] border-[var(--outline-default)]",
              )}
              style={{
                maxHeight: `${dynamicMaxHeight}px`,
              }}
            >
              {renderAutocompleteOptions()}
            </div>
          ) : null)}
      </div>
    </ClickAwayListener>
  );
});

type FilterOptionsState<Value> = {
  inputValue: string;
  getOptionLabel: (option: Value) => string;
};

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

// Given a label and a search term, break the label into little spans that highlight the parts matching the search term.
const highlightLabelParts = (label: string, searchTerm: string, enabled: boolean) => {
  if (!enabled) return label;
  if (!searchTerm) return [label];

  // Split searchTerm into words, escape each for regex
  const terms = searchTerm.trim().split(/\s+/).filter(Boolean).map(escapeRegExp);
  if (terms.length === 0) return [label];

  // Build a regex that matches any term as a whole word (case-insensitive)
  const regex = new RegExp(`(${terms.join("|")})`, "gi");

  // Split label by regex, highlight matches
  return label.split(regex).map((part, idx) =>
    terms.some((term) => new RegExp(`^${term}$`, "i").test(part)) ? (
      <span key={idx.toString()} className="bg-primary/60 text-white">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

const filterOptions = (options: unknown[], { inputValue }: FilterOptionsState<unknown>) => {
  const searchTerms = inputValue.trim().toLowerCase().split(/\s+/).map(escapeRegExp);

  return (options as TOption[]).filter((option) => {
    if (option.hidden) return false;
    const combinedFields = [option.label.toLowerCase(), option.groupValue.toLowerCase()].join(" ");

    return searchTerms.every((term) => {
      const specialCharRegex = /[^a-z0-9\s]/i;
      let regex: RegExp;

      if (specialCharRegex.test(term)) {
        regex = new RegExp(`(^|\\W)${term}`, "i");
        return regex.test(combinedFields);
      }
      regex = new RegExp(`(^|\\W)${term}`, "i");
      return regex.test(combinedFields);
    });
  });
};
